import usePatientPhotos from "@/features/clinical-actions/usePatientPhotos.js";
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import BasicDialogCritical from "@/components/ui/BasicDialogCritical.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import Divider from "@/components/ui/Divider.jsx";
import { supabase } from "@/services/supabaseClient";
import { loadDoctorAppointmentSnapshot } from "@/pages/doctors/appointments/lib/doctorAppointmentsData.js";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { pickDiagnosisNotes, pickDiagnosisTitle } from "@/utils/diagnosis.js";
import { resolvePatientFacingDoctorName } from "@/utils/doctorPatientFacingName.js";
import { buildPrescriptionDosageLabel } from "@/utils/prescriptionSchedule.js";
import { revokeAppointmentEhrAccess } from "@/services/ehrAccessService.js";
import { normalizeVerificationStatus } from "@/utils/verificationStatus.js";

const ICONS = {
  verified: "/icons/doctor/appointments/verified.svg",
  pendingVerification: "/icons/pending-verification.svg",
  schedule: "/icons/list-items/schedule_Fill.svg",
  service: "/icons/doctor/medical_services.svg",
  hospital: "/icons/doctor/appointments/hospital_Fill.svg",
  diagnosis: "/icons/list-items/diagnosis_fill.svg",
  file: "/icons/list-items/description_fill.svg",
  image: "/icons/list-items/imagesmode.svg",
  pill: "/icons/list-items/pill_Fill.svg",
};

function safeDate(value) {
  const d = value ? new Date(value) : null;
  return d && !Number.isNaN(d.getTime()) ? d : null;
}

function pad2(value) {
  return String(value).padStart(2, "0");
}

function formatDisplayTime(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/[ap]m/i.test(raw)) return raw.replace(/\s+/g, " ").trim();

  const match = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return raw;

  const hours24 = Number(match[1]);
  const minutes = match[2];
  if (!Number.isFinite(hours24)) return raw;

  const suffix = hours24 >= 12 ? "PM" : "AM";
  const hours12 = hours24 % 12 || 12;
  return `${hours12}:${minutes} ${suffix}`;
}

function plusMinutesHHMM(hhmm, minutesToAdd = 0) {
  const [hRaw, mRaw] = String(hhmm || "").split(":");
  const hours = Number(hRaw);
  const minutes = Number(mRaw);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return "";
  const total = hours * 60 + minutes + Number(minutesToAdd || 0);
  const wrapped = ((total % 1440) + 1440) % 1440;
  return `${pad2(Math.floor(wrapped / 60))}:${pad2(wrapped % 60)}`;
}

function toDateKeyLocal(value) {
  const d = safeDate(value);
  if (!d) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function displayValue(value, fallback = "—") {
  if (value === null || value === undefined) return fallback;
  if (Array.isArray(value)) {
    const items = value.map((item) => String(item || "").trim()).filter(Boolean);
    return items.length ? items.join(", ") : fallback;
  }
  const text = String(value).trim();
  return text || fallback;
}

function pickFirstText(...values) {
  for (const value of values) {
    if (Array.isArray(value)) {
      const joined = value.map((item) => String(item || "").trim()).filter(Boolean).join(", ");
      if (joined) return joined;
      continue;
    }

    const text = String(value || "").trim();
    if (text) return text;
  }
  return "";
}

function formatCompactLongDate(dateValue, locale = "en-US", fallback = "—") {
  const date = dateValue ? new Date(`${String(dateValue).slice(0, 10)}T00:00:00`) : null;
  if (!date || Number.isNaN(date.getTime())) return fallback;

  return new Intl.DateTimeFormat(locale, {
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(date);
}

function normalizeDoctorDisplayName(name, fallback = "Doctor") {
  const resolved = resolvePatientFacingDoctorName(name);
  return resolved === "Doctor" ? fallback : resolved;
}

async function resolveDoctorAvatar(client, primaryUrl, photoPath, fallback = "/images/demo/doctors/avatar-fallback.jpg") {
  const direct = String(primaryUrl || "").trim();
  if (direct) return direct;

  const cleanPath = String(photoPath || "").trim();
  if (cleanPath) {
    if (/^https?:\/\//i.test(cleanPath)) return cleanPath;
    try {
      const { data, error } = await client.storage
        .from("doctor-profile-images")
        .createSignedUrl(cleanPath, 60 * 60 * 24);
      if (!error && data?.signedUrl) return data.signedUrl;
    } catch {
      // ignore and fall through
    }
  }

  return fallback;
}

function doctorSpecialtyText(value) {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean).join(", ");
  }
  return String(value || "").trim();
}

function isWeakDoctorValue(value, weakValues = []) {
  const text = String(value || "").trim().toLowerCase();
  return !text || weakValues.includes(text);
}

function pickDoctorName(fallback, ...candidates) {
  for (const candidate of candidates) {
    const resolved = normalizeDoctorDisplayName(candidate, fallback);
    if (!isWeakDoctorValue(resolved, ["doctor"])) return resolved;
  }
  return fallback;
}

function pickDoctorSpecialty(fallback, ...candidates) {
  for (const candidate of candidates) {
    const text = doctorSpecialtyText(candidate);
    if (!isWeakDoctorValue(text, ["doctor", "general practitioner", "dermatologist"])) {
      return text;
    }
  }
  return fallback;
}

function buildFallbackSnapshot(row, restored, patient) {
  const base = row && typeof row === "object" ? row : {};
  const startAt = safeDate(base?.appointment_date);
  const start =
    String(base?.start || base?.selectedTime || "").trim() ||
    (startAt ? `${pad2(startAt.getHours())}:${pad2(startAt.getMinutes())}` : "");
  const dateISO =
    String(base?.dateISO || base?.selectedDate || "").trim() ||
    toDateKeyLocal(base?.appointment_date);
  const duration = Number(base?.duration_minutes || base?.when?.duration_mins || 15);

  return {
    ...(restored || {}),
    ...base,
    id: String(base?.id || restored?.id || ""),
    appointmentId: String(base?.appointmentId || base?.id || restored?.appointmentId || ""),
    status: String(base?.status || restored?.status || "completed").toLowerCase(),
    consultation_type: base?.consultation_type || restored?.consultation_type || null,
    subtitle:
      base?.subtitle ||
      restored?.subtitle ||
      (String(base?.consultation_type || "").toLowerCase() === "private"
        ? "Virtual"
        : "First time consultancy"),
    dateISO: dateISO || restored?.dateISO || restored?.selectedDate || null,
    selectedDate: dateISO || restored?.selectedDate || null,
    start,
    selectedTime: start || restored?.selectedTime || "",
    end:
      String(base?.end || "").trim() ||
      plusMinutesHHMM(start || restored?.start || restored?.selectedTime || "", duration) ||
      restored?.end ||
      "",
    notes: pickFirstText(base?.notes, restored?.notes),
    diagnosisTitle: pickFirstText(base?.diagnosisTitle, restored?.diagnosisTitle),
    diagnosisNotes: pickFirstText(base?.diagnosisNotes, restored?.diagnosisNotes),
    facilityName:
      base?.facilityName ||
      base?.facility?.name ||
      restored?.facilityName ||
      restored?.facility?.name ||
      restored?.facility_name ||
      "",
    patient: base?.patient || patient || restored?.patient || null,
    patientId:
      base?.patientId ||
      base?.patient_id ||
      base?.patient_user_id ||
      restored?.patientId ||
      restored?.patient_id ||
      restored?.patient_user_id ||
      patient?.id ||
      patient?.user_id ||
      null,
  };
}

function readStoredDraft(storage, key) {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function loadAppointmentDraft(appointmentId) {
  const key = `afrimed:doctor_appt:${String(appointmentId)}`;
  return (
    readStoredDraft(window.sessionStorage, key) ||
    readStoredDraft(window.localStorage, key) ||
    null
  );
}

function loadPrescriptionDraft(appointmentId) {
  const key = `afrimed:rx:draft:${String(appointmentId)}`;
  return (
    readStoredDraft(window.sessionStorage, key) ||
    readStoredDraft(window.localStorage, key) ||
    null
  );
}

function mergeSnapshots(...sources) {
  const arrayKeys = ["labResults", "testResults", "attachments", "patientPhotos"];

  return sources.reduce((acc, source) => {
    if (!source || typeof source !== "object") return acc;

    const next = {
      ...(acc || {}),
      ...source,
      patient: {
        ...(acc?.patient || {}),
        ...(source?.patient || {}),
      },
      patientProfile: {
        ...(acc?.patientProfile || {}),
        ...(source?.patientProfile || {}),
      },
      ehrSummary: {
        ...(acc?.ehrSummary || {}),
        ...(source?.ehrSummary || {}),
      },
      facility: {
        ...(acc?.facility || {}),
        ...(source?.facility || {}),
      },
    };

    arrayKeys.forEach((key) => {
      if (Array.isArray(source?.[key]) && source[key].length > 0) {
        next[key] = source[key];
      }
    });

    return next;
  }, null);
}

function resolveFrequencyLabel(meta, draft, fallback = "—") {
  if (meta?.frequencyLabel) return meta.frequencyLabel;
  if (draft?.schedule?.everyHours) return `Every ${draft.schedule.everyHours}hrs`;
  return fallback;
}

function resolvePrescriptionDetailLabel(meta, draft, fallback = "—") {
  const dosageLabel = buildPrescriptionDosageLabel({
    ...(draft || {}),
    ...(meta || {}),
    schedule: {
      ...(draft?.schedule || {}),
      ...(meta?.schedule || {}),
    },
  });
  if (dosageLabel) return dosageLabel;

  const frequencyType = String(
    meta?.frequencyType || draft?.schedule?.frequencyType || draft?.frequencyType || ""
  )
    .trim()
    .toLowerCase();

  if (frequencyType !== "cyclical") return fallback;

  const useDays = meta?.cycleUseDays || draft?.schedule?.cycleUseDays || null;
  const pauseDays = meta?.cyclePauseDays || draft?.schedule?.cyclePauseDays || null;
  if (useDays && pauseDays) return `For ${useDays} Days, Pause for ${pauseDays} Days`;
  return fallback;
}

function buildDiagnosisContent(
  primaryDiagnosis,
  bodyNotes,
  {
    titleFallback = "No diagnosis recorded",
    bodyFallback = "No diagnosis notes were added for this appointment.",
  } = {}
) {
  const title = pickFirstText(primaryDiagnosis);
  return {
    title: title || titleFallback,
    body: title ? pickFirstText(bodyNotes) || bodyFallback : bodyFallback,
  };
}

function buildLabItems(snapshot, fallbackTitle = "No tests or lab results recorded") {
  const items = [
    ...(Array.isArray(snapshot?.labResults) ? snapshot.labResults : []),
    ...(Array.isArray(snapshot?.testResults) ? snapshot.testResults : []),
    ...(Array.isArray(snapshot?.attachments) ? snapshot.attachments : []),
  ]
    .map((item, index) => {
      if (!item) return null;

      const title = pickFirstText(item?.name, item?.title, item?.fileName);
      if (!title) return null;

      return {
        id: String(item?.id || `state-item-${index}`),
        title,
        subtitle: pickFirstText(item?.source, item?.by, item?.author),
        icon: String(item?.type || "").toLowerCase().includes("image") ? ICONS.image : ICONS.file,
      };
    })
    .filter(Boolean);

  if (items.length > 0) return items.slice(0, 2);

  return [{ id: "no-labs", title: fallbackTitle, subtitle: "", icon: ICONS.file }];
}

function ListSection({ title, children }) {
  return (
    <div className="mt-5">
      <div className="mb-2 text-body-medium text-on-surface-var">{title}</div>
      {children}
    </div>
  );
}

function SupportingRow({ icon, title, subtitle, withDivider = true }) {
  return (
    <>
      <div className="px-4 py-3 flex items-center gap-3">
        <img src={icon} alt="" className="h-6 w-6 opacity-80 shrink-0" draggable="false" />
        <div className="min-w-0 flex-1">
          <div className="text-body-large text-on-surface truncate">{title}</div>
          {subtitle ? <div className="text-body-medium text-on-surface-var truncate">{subtitle}</div> : null}
        </div>
      </div>
      {withDivider ? <Divider inset variant="outlineVariant" /> : null}
    </>
  );
}

export default function DoctorPastAppointmentDetails() {
  const nav = useNavigate();
  const location = useLocation();
  const { appointmentId, patientId } = useParams();
  const { t, languageCode } = useLanguage();

  const state = location.state || {};
  const restored = state?.restored || null;
  const patient = state?.patient || restored?.patient || null;
  const pastAppointment = state?.pastAppointment || null;
  const pastAppointmentSnapshot = state?.pastAppointmentSnapshot || null;
  const pastDiagnosis = state?.pastDiagnosis || "";

  const appointmentDraft = React.useMemo(() => loadAppointmentDraft(appointmentId), [appointmentId]);
  const draftPrescription = React.useMemo(() => loadPrescriptionDraft(appointmentId), [appointmentId]);
  const fallbackSnapshot = React.useMemo(
    () =>
      mergeSnapshots(
        buildFallbackSnapshot(pastAppointment, restored, patient),
        pastAppointmentSnapshot,
        restored,
        appointmentDraft
      ),
    [appointmentDraft, pastAppointment, pastAppointmentSnapshot, patient, restored]
  );

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [snapshot, setSnapshot] = React.useState(() => fallbackSnapshot);
  const [doctorDisplay, setDoctorDisplay] = React.useState(null);
  const [prescription, setPrescription] = React.useState(null);
  const {items:photoItems}=usePatientPhotos(appointmentId);
  const [completeDialogOpen, setCompleteDialogOpen] = React.useState(false);
  const [completeBusy, setCompleteBusy] = React.useState(false);
  const [completeError, setCompleteError] = React.useState("");

  React.useEffect(() => {
    setSnapshot((current) => mergeSnapshots(current, fallbackSnapshot) || fallbackSnapshot);
  }, [fallbackSnapshot]);

  React.useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const {
          data: { user },
          error: authError,
        } = await supabase.auth.getUser();

        if (authError || !user?.id) {
          throw new Error(
            t("doctors.pastAppointmentDetails.errors.auth", "Authentication required.")
          );
        }

        const doctorId = String(user.id);

        const [dbSnapshot, doctorProfileRes, authProfileRes, publicDoctorRes, latestPrescriptionRes] = await Promise.all([
          loadDoctorAppointmentSnapshot({ appointmentId, doctorId, supabase }),
          supabase
            .from("doctor_profiles")
            .select("user_id, full_name, specialization, specialty, profile_image_url, profile_photo_path, verified_status")
            .eq("user_id", doctorId)
            .maybeSingle(),
          supabase
            .from("profiles")
            .select("id, user_id, display_name, avatar_url")
            .eq("user_id", doctorId)
            .maybeSingle(),
          supabase
            .from("v_doctor_directory_browse")
            .select("doctor_user_id, display_name, specialty, verified_status, profile_photo_path")
            .eq("doctor_user_id", doctorId)
            .maybeSingle(),
          supabase
            .from("prescriptions")
            .select("id, issued_at, diagnosis, notes, meta, status")
            .eq("appointment_id", appointmentId)
            .order("issued_at", { ascending: false })
            .limit(1),
        ]);

        const nextSnapshot = mergeSnapshots(dbSnapshot, fallbackSnapshot);
        if (!nextSnapshot) {
          throw new Error("Past appointment not found.");
        }

        if (!alive) return;
        setSnapshot(nextSnapshot);
        const doctorProfile = doctorProfileRes?.data || null;
        const authProfile = authProfileRes?.data || null;
        const publicDoctor = publicDoctorRes?.data || null;
        const snapshotDoctor = nextSnapshot?.doctor || null;
        const resolvedAvatarUrl = await resolveDoctorAvatar(
          supabase,
          doctorProfile?.profile_image_url || snapshotDoctor?.avatar || "",
          doctorProfile?.profile_photo_path || publicDoctor?.profile_photo_path,
          "/images/demo/doctors/avatar-fallback.jpg"
        );
        const verificationStatus = normalizeVerificationStatus(
          snapshotDoctor?.verificationStatus ||
            publicDoctor?.verified_status ||
            doctorProfile?.verified_status,
          snapshotDoctor?.verified ? "verified" : "pending"
        );
        setDoctorDisplay({
          name: pickDoctorName(
            t("doctors.pastAppointmentDetails.doctorFallback", "Doctor"),
            snapshotDoctor?.name,
            publicDoctor?.display_name,
            doctorProfile?.full_name,
            authProfile?.display_name
          ),
          specialty: pickDoctorSpecialty(
            t("doctors.pastAppointmentDetails.specialtyFallback", "Specialist"),
            snapshotDoctor?.specialty,
            publicDoctor?.specialty,
            doctorProfile?.specialization,
            doctorProfile?.specialty
          ),
          avatar:
            resolvedAvatarUrl ||
            doctorProfile?.profile_image_url ||
            authProfile?.avatar_url ||
            snapshotDoctor?.avatar ||
            "/images/demo/doctors/avatar-fallback.jpg",
          verificationStatus: verificationStatus || (snapshotDoctor?.verified ? "verified" : ""),
          verified: verificationStatus === "verified" || Boolean(snapshotDoctor?.verified),
        });

        const latestPrescription = Array.isArray(latestPrescriptionRes?.data)
          ? latestPrescriptionRes.data[0] || null
          : null;
        setPrescription(latestPrescription);
      } catch (e) {
        if (!alive) return;
        setError(
          e?.message ||
            t(
              "doctors.pastAppointmentDetails.errors.load",
              "Failed to load past appointment details."
            )
        );
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    window.addEventListener("afrimed:appointments:changed", load);
    return () => {
      alive = false;
      window.removeEventListener("afrimed:appointments:changed", load);
    };
  }, [appointmentId, patientId, fallbackSnapshot]);

  const rxMeta = prescription?.meta && typeof prescription.meta === "object" ? prescription.meta : {};
  const resolvedPhotos = photoItems.slice(0, 2);

  async function handleCompleteConsultation() {
    const nextPatientId =
      patientId ||
      snapshot?.patientId ||
      snapshot?.patient_id ||
      snapshot?.patient_user_id ||
      snapshot?.patient?.user_id ||
      snapshot?.patient?.id ||
      null;

    setCompleteBusy(true);
    setCompleteError("");

    try {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      const doctorId = String(user?.id || "").trim();
      if (authError || !doctorId) {
        throw new Error(
          t("doctors.pastAppointmentDetails.errors.auth", "Authentication required.")
        );
      }

      if (appointmentId && nextPatientId) {
        await revokeAppointmentEhrAccess({
          appointmentId,
          doctorId,
          doctorRef: doctorId,
          patientUserId: nextPatientId,
          patientRef:
            snapshot?.patient_row_id ||
            snapshot?.patient_id ||
            snapshot?.patientId ||
            snapshot?.patient?.patient_id ||
            snapshot?.patient?.id ||
            null,
        });
      }

      window.dispatchEvent(new Event("afrimed:appointments:changed"));
      setCompleteDialogOpen(false);

      nav(`/appointments/summary/${appointmentId}`, {
      state: {
        actorRole: "doctor",
        appointmentId,
        patientId: nextPatientId,
        patient: patient || snapshot?.patient || null,
        restored: {
          ...(snapshot || restored || {}),
          ehrAccessStatus: "revoked",
          ehrRevokedAt: new Date().toISOString(),
        },
        counterpartName:
          snapshot?.patient?.full_name ||
          snapshot?.patient?.name ||
          patient?.full_name ||
          patient?.name ||
          "Patient",
        counterpartAvatar:
          snapshot?.patient?.avatar_url ||
          snapshot?.patient?.profile_image_url ||
          patient?.avatar_url ||
          patient?.profile_image_url ||
          "/icons/circular-avatar-placeholder.svg",
        doctorName,
        doctor: {
          name: doctorName,
          full_name: doctorName,
          photo_url: doctorAvatar,
        },
        prescriptionId: prescription?.id || null,
        endedAt: new Date().toISOString(),
      },
    });
    } catch (nextError) {
      setCompleteError(
        nextError?.message ||
          t(
            "doctors.pastAppointmentDetails.errors.completeConsultation",
            "Could not complete consultation right now."
          )
      );
    } finally {
      setCompleteBusy(false);
    }
  }

  const doctorName = normalizeDoctorDisplayName(
    doctorDisplay?.name || "Doctor",
    t("doctors.pastAppointmentDetails.doctorFallback", "Doctor")
  );
  const doctorSpecialty =
    doctorDisplay?.specialty ||
    t("doctors.pastAppointmentDetails.specialtyFallback", "Specialist");
  const doctorAvatar = doctorDisplay?.avatar || "/images/demo/doctors/avatar-fallback.jpg";
  const doctorVerificationStatus = String(doctorDisplay?.verificationStatus || "").trim().toLowerCase();

  const timeStart = formatDisplayTime(snapshot?.start || snapshot?.selectedTime || "");
  const timeEnd = formatDisplayTime(snapshot?.end || "");
  const timeLabel = [timeStart, timeEnd].filter(Boolean).join(" - ") || "—";
  const dateLabel = formatCompactLongDate(
    snapshot?.dateISO || snapshot?.selectedDate || snapshot?.appointment_date,
    languageCode,
    "—"
  );
  const serviceLabel = displayValue(snapshot?.serviceName || snapshot?.subtitle || snapshot?.appointment_type, "—");
  const facilityLabel = displayValue(snapshot?.facilityName || snapshot?.facility?.name, "—");

  const diagnosisTitleRaw =
    pickDiagnosisTitle(
      appointmentDraft?.diagnosisTitle,
      appointmentDraft?.diagnosis,
      snapshot?.diagnosisTitle,
      snapshot?.diagnosis,
      draftPrescription?.diagnosisTitle,
      draftPrescription?.diagnosis,
      rxMeta?.diagnosisTitle,
      rxMeta?.diagnosis,
      prescription?.diagnosis,
    ) || "";
  const diagnosisBodyRaw =
    pickDiagnosisNotes(
      rxMeta?.diagnosisBody,
      draftPrescription?.diagnosisNotes,
      appointmentDraft?.diagnosisNotes,
      snapshot?.diagnosisNotes,
    ) || "";
  const diagnosis = buildDiagnosisContent(diagnosisTitleRaw, diagnosisBodyRaw, {
    titleFallback: t(
      "doctors.pastAppointmentDetails.diagnosisFallback",
      "No diagnosis recorded"
    ),
    bodyFallback: t(
      "doctors.pastAppointmentDetails.diagnosisBodyFallback",
      "No diagnosis notes were added for this appointment."
    ),
  });

  const medicationNameRaw = pickFirstText(
    rxMeta?.medicineName,
    draftPrescription?.medication?.name,
    draftPrescription?.medicineName
  );
  const hasPrescription =
    Boolean(
      prescription?.id ||
        medicationNameRaw ||
        pickFirstText(
          rxMeta?.frequencyLabel,
          rxMeta?.specialInstructions,
          draftPrescription?.schedule?.everyHours,
          draftPrescription?.notes
        )
    );
  const medicationName =
    medicationNameRaw ||
    t("doctors.pastAppointmentDetails.prescriptionNone", "No prescription issued");
  const medicationType = pickFirstText(
    rxMeta?.medicineType,
    draftPrescription?.medication?.type
  );
  const frequencyLabel = hasPrescription
    ? resolveFrequencyLabel(rxMeta, draftPrescription, "—")
    : "—";
  const detailLabel = hasPrescription
    ? resolvePrescriptionDetailLabel(rxMeta, draftPrescription, "—")
    : "—";
  const guidanceText =
    (hasPrescription &&
      pickFirstText(
        rxMeta?.specialInstructions,
        rxMeta?.directions,
        rxMeta?.guidance,
        draftPrescription?.notes,
        prescription?.notes
      )) ||
    t(
      "doctors.pastAppointmentDetails.prescriptionGuidanceFallback",
      "No prescription instructions recorded."
    );

  const patientNotes = displayValue(
    pickFirstText(snapshot?.notes, appointmentDraft?.notes),
    t("doctors.pastAppointmentDetails.patientNotesFallback", "No patient notes recorded.")
  );
  const labItems = buildLabItems(
    snapshot,
    t(
      "doctors.pastAppointmentDetails.labsFallback",
      "No tests or lab results recorded"
    )
  );

  if (loading && !snapshot) {
    return (
      <div data-hide-tabbar="true" className="min-h-[100dvh] bg-[rgb(var(--clr-surface))]">
        <div className="afrimed-rail">
          <TopBarCenter
            title={t("doctors.pastAppointmentDetails.title", "Appointment details")}
            onBack={() => nav(-1)}
          />
          <div className="pt-6 text-body-medium text-on-surface-var">
            {t(
              "doctors.pastAppointmentDetails.loading",
              "Loading appointment details..."
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!snapshot || error) {
    return (
      <div data-hide-tabbar="true" className="min-h-[100dvh] bg-[rgb(var(--clr-surface))]">
        <div className="afrimed-rail">
          <TopBarCenter
            title={t("doctors.pastAppointmentDetails.title", "Appointment details")}
            onBack={() => nav(-1)}
          />
          <div className="pt-6 text-body-medium text-[#b42318]">
            {error ||
              t(
                "doctors.pastAppointmentDetails.notFound",
                "Past appointment not found."
              )}
          </div>
        </div>
      </div>
    );
  }

  const restoredWithPhotos = {
    ...(snapshot || {}),
    patientPhotos: Array.isArray(photoItems) ? photoItems : [],
  };

  return (
    <div data-hide-tabbar="true" className="min-h-[100dvh] bg-[rgb(var(--clr-surface))]">
      <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
        <TopBarCenter
          title={t("doctors.pastAppointmentDetails.title", "Appointment details")}
          onBack={() => nav(-1)}
          nakedBack
        />

        <div className="mt-4 rounded-[12px] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.06)] overflow-hidden">
          <div className="px-4 py-3 flex items-center gap-3">
            <img
              src={doctorAvatar}
              alt={doctorName}
              className="h-[68px] w-[68px] rounded-[8px] border border-white object-cover shadow-[0_0_0_1px_rgba(24,50,119,0.10)]"
              draggable="false"
            />

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <div className="truncate text-body-large text-on-surface">{doctorName}</div>
                {doctorVerificationStatus === "verified" || doctorVerificationStatus === "pending" ? (
                  <img
                    src={
                      doctorVerificationStatus === "verified"
                        ? ICONS.verified
                        : ICONS.pendingVerification
                    }
                    alt=""
                    className="h-4 w-4 shrink-0"
                    draggable="false"
                  />
                ) : null}
              </div>
              <div className="text-body-medium text-on-surface-var">{doctorSpecialty}</div>
            </div>
          </div>

          <Divider inset variant="outlineVariant" />
          <SupportingRow icon={ICONS.schedule} title={timeLabel} subtitle={dateLabel} />
          <SupportingRow icon={ICONS.service} title={serviceLabel} />
          <SupportingRow icon={ICONS.hospital} title={facilityLabel} withDivider={false} />
        </div>

        <ListSection title={t("doctors.pastAppointmentDetails.sections.diagnosis", "Diagnosis")}>
          <div className="rounded-[12px] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.06)] overflow-hidden">
            <div className="px-4 py-3 flex items-start gap-3">
              <img src={ICONS.diagnosis} alt="" className="h-6 w-6 opacity-90 shrink-0" draggable="false" />
              <div className="min-w-0 flex-1">
                <div className="text-body-large text-on-surface">{diagnosis.title}</div>
                <div className="mt-0.5 text-body-medium leading-5 text-on-surface-var">
                  {diagnosis.body}
                </div>
              </div>
            </div>
          </div>
        </ListSection>

        <ListSection
          title={t(
            "doctors.pastAppointmentDetails.sections.labs",
            "Tests & Lab results"
          )}
        >
          <div className="rounded-[12px] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.06)] overflow-hidden">
            {labItems.map((item, index) => (
              <React.Fragment key={item.id}>
                <SupportingRow
                  icon={item.icon}
                  title={item.title}
                  subtitle={item.subtitle}
                  withDivider={index < labItems.length - 1}
                />
              </React.Fragment>
            ))}
          </div>
        </ListSection>

        <ListSection title={t("doctors.pastAppointmentDetails.sections.prescription", "Prescription")}>
          <div className="rounded-[12px] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.06)] overflow-hidden">
            <SupportingRow icon={ICONS.pill} title={medicationName} subtitle={medicationType} />
            <div className="px-4 py-2 text-body-large text-on-surface-var">{frequencyLabel}</div>
            <Divider inset variant="outlineVariant" />
            <div className="px-4 py-2 text-body-large text-on-surface-var">{detailLabel}</div>
            <Divider inset variant="outlineVariant" />
            <div className="px-4 py-3 text-body-medium leading-5 text-on-surface-var">{guidanceText}</div>
          </div>
        </ListSection>

        <ListSection title={t("doctors.pastAppointmentDetails.sections.notes", "Patient Notes")}>
          <div className="rounded-[12px] bg-white px-4 py-3 shadow-[0_1px_2px_rgba(15,23,42,0.06)]">
            <div className="text-body-large text-on-surface">{patientNotes}</div>
          </div>
        </ListSection>

        <div className="mt-5">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-body-medium text-on-surface-var">
              {t("doctors.pastAppointmentDetails.sections.photos", "Patient Photos")}
            </div>
            <button
              type="button"
              onClick={() => nav(`/doctors/appointments/${appointmentId}/patientphotos`, { state: restoredWithPhotos })}
              className="text-title-small text-[rgb(var(--afrimed-primary))]"
            >
              {t("doctors.pastAppointmentDetails.showAll", "Show all")}
            </button>
          </div>

          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            {resolvedPhotos.length > 0 ? (
              resolvedPhotos.map((item, index) => (
                <div
                  key={String(item.id || index)}
                  className={index === 0 ? "h-[205px] w-[296px] shrink-0 overflow-hidden rounded-[28px]" : "h-[205px] w-[56px] shrink-0 overflow-hidden rounded-[28px]"}
                >
                  <img
                    src={item.url}
                    alt="Patient photo"
                    className="h-full w-full object-cover"
                    draggable="false"
                  />
                </div>
              ))
            ) : (
              <div className="w-full rounded-[12px] bg-white px-4 py-4 text-body-medium text-on-surface-var shadow-[0_1px_2px_rgba(15,23,42,0.06)]">
                {t(
                  "doctors.pastAppointmentDetails.photosEmpty",
                  "No patient photos shared yet."
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mt-5">
          {completeError ? (
            <div className="mb-3 rounded-[12px] bg-[rgba(180,35,24,0.08)] px-4 py-3 text-body-medium text-[#b42318]">
              {completeError}
            </div>
          ) : null}
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => setCompleteDialogOpen(true)}
              disabled={completeBusy}
              className="flex h-12 w-full max-w-[390px] items-center justify-center gap-2 rounded-[12px] bg-[rgba(77,163,255,0.12)] px-4 text-[rgb(var(--afrimed-primary))] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <img
                src="/icons/appointments/check.svg"
                alt=""
                className="h-[18px] w-[18px]"
                draggable="false"
              />
              <span className="text-label-large">
                {completeBusy
                  ? t("common.updating", "Updating...")
                  : t("doctors.pastAppointmentDetails.completeConsultation", "Complete consultation")}
              </span>
            </button>
          </div>
        </div>
      </div>

      <BasicDialogCritical
        open={completeDialogOpen}
        title={t("doctors.pastAppointmentDetails.completeDialog.title", "Complete consultation")}
        iconSrc="/icons/check-ok.svg"
        body={t(
          "doctors.pastAppointmentDetails.completeDialog.body",
          "The patient's EHR will no longer be accessible. To access the patient's EHR again, you will need to Request Access."
        )}
        cancelText={t("common.cancel", "Cancel")}
        okText={
          completeBusy
            ? t("common.updating", "Updating...")
            : t("doctors.pastAppointmentDetails.completeDialog.confirmShort", "Complete")
        }
        onCancel={() => {
          if (!completeBusy) setCompleteDialogOpen(false);
        }}
        onOk={handleCompleteConsultation}
        disableBackdropClose={completeBusy}
      />
    </div>
  );
}
